import { Box, Stack } from '@mui/material';
import { compact } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { Accordion } from '@/components/Accordion/Accordion';
import { Button } from '@/components/form/baseInputs/Button/Button';
import { DragAndDropList } from '@/components/lists/DragAndDropList/DragAndDropList';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { HeaderCellTypography } from '@/components/tables/components/HeaderCellTypography';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { StandaloneDocumentUploaderModal } from '@/modules/documents/DocumentUploaderModal/StandaloneDocumentUploaderModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { COLORS } from '@/styles/tokens/colors';
import { DocumentType } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { localStorageBooleanSerializer } from '@/utils/localStorageSerializers';

import { AmendmentItem } from './components/AmendmentItem';
import {
  type DocumentChange,
  ManageDocumentsPopover,
} from './components/ManageDocumentsPopover';
import { useDocumentDetailsLazyQuery } from './graphql/GetMultipleDocuments.generated';
import { useManageDocumentAmendments } from './hooks/useManageDocumentAmendments';
import { useVersionableDocuments } from './hooks/useVersionableDocuments';
import { SelectableDocument } from './types';
import {
  getCalloutInfo,
  getCompatibleDocumentTypes,
  getEligibleDocumentTypes,
  hasDocumentOrderChanged,
  willVersionChangeGenerateCombinedSummary,
} from './utils/ManageDocumentAmendments.utils';

interface ManageDocumentAmendmentsProps {
  documentId: string;
  currentDocumentType: DocumentType;
  associatedDocuments?: SelectableDocument[];
  onSummaryStarted: () => void;
}

// Get document types that are eligible for "version management"
const ELIGIBLE_DOCUMENT_TYPES = getEligibleDocumentTypes();

interface AmendmentDocument {
  id: string;
  name: string;
  type: DocumentType;
  isCurrentDocument: boolean;
}

export function ManageDocumentAmendments({
  documentId,
  currentDocumentType,
  associatedDocuments,
  onSummaryStarted,
}: ManageDocumentAmendmentsProps) {
  const { showFeedback } = useFeedback();
  const { householdId } = useHouseholdDetailsContext();
  const { updateDocumentVersions, loading: isSavingDocumentVersions } =
    useManageDocumentAmendments(documentId);
  const canEditHouseholdData = useCanEditHouseholdData();

  const [isUploaderOpen, setUploaderOpen] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<
    AmendmentDocument[] | null
  >(null);

  const [getDocumentDetails] = useDocumentDetailsLazyQuery();

  const handleAddNewDocument = useCallback(() => {
    setUploaderOpen(true);
  }, []);

  const { selectable } = useVersionableDocuments(
    householdId,
    documentId,
    currentDocumentType
  );

  const aiCapabilitiesEnabled = useAICapabilitiesEnabled();

  const [accordionExpanded, setAccordionExpanded] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.DOCUMENT_DETAILS_AMENDMENTS_ACCORDION_EXPANDED,
    true,
    localStorageBooleanSerializer
  );

  const handleAccordionChange = useCallback(
    (expanded: boolean) => {
      setAccordionExpanded(expanded);
    },
    [setAccordionExpanded]
  );

  // Check if the current document is eligible for the "management amendments"
  // functionality
  const isEligible = ELIGIBLE_DOCUMENT_TYPES.includes(currentDocumentType);

  // Check if the current document is a will-related document
  const isWillDocument = [DocumentType.Will, DocumentType.WillCodicil].includes(
    currentDocumentType
  );

  // Get all related documents (previous and next versions)
  const relatedDocuments = useMemo(() => {
    if (!associatedDocuments) return [];

    const documentChain: AmendmentDocument[] = compact(
      associatedDocuments.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
        isCurrentDocument: doc.id === documentId,
      }))
    );

    return documentChain;
  }, [associatedDocuments, documentId]);

  const displayedDocuments = pendingDocuments ?? relatedDocuments;
  const displayedDocumentIds = useMemo(
    () => displayedDocuments.map((document) => document.id),
    [displayedDocuments]
  );
  const hasPendingChanges =
    pendingDocuments !== null &&
    hasDocumentOrderChanged(relatedDocuments, pendingDocuments);
  const willGenerateCombinedSummary =
    pendingDocuments !== null &&
    willVersionChangeGenerateCombinedSummary(
      relatedDocuments,
      pendingDocuments
    );

  // Get callout information (whether to show and the document type name)
  const { shouldShowCallout, primaryDocumentTypeName } = useMemo(() => {
    return getCalloutInfo(displayedDocuments, currentDocumentType);
  }, [displayedDocuments, currentDocumentType]);

  const handleUploaderClose = useCallback(
    async (documentIds: string[]) => {
      setUploaderOpen(false);

      if (documentIds.length > 0) {
        try {
          const { data } = await getDocumentDetails({
            variables: { documentIds },
          });

          if (!data?.documents) return;

          const compatibleTypes =
            getCompatibleDocumentTypes(currentDocumentType);
          const compatibleDocuments: AmendmentDocument[] = getNodes(
            data.documents
          )
            .filter((doc) => compatibleTypes.includes(doc.type))
            .map((doc) => ({
              id: doc.id,
              name: doc.name,
              type: doc.type,
              isCurrentDocument: false,
            }));

          // Add compatible documents to version chain
          if (compatibleDocuments.length > 0) {
            const existingIds = new Set(
              displayedDocuments.map((document) => document.id)
            );
            const newDocuments = compatibleDocuments.filter(
              (doc) => !existingIds.has(doc.id)
            );

            if (newDocuments.length > 0) {
              setPendingDocuments([...newDocuments, ...displayedDocuments]);
            }
          }
        } catch (err) {
          diagnostics.error(
            'Error processing uploaded documents',
            err as Error
          );
          showFeedback(
            'Failed to process uploaded documents. Please associate them manually.'
          );
        }
      }
    },
    [currentDocumentType, displayedDocuments, getDocumentDetails, showFeedback]
  );

  // Hide if AI is disabled, document is not eligible, or no data
  if (!aiCapabilitiesEnabled || !isEligible || !associatedDocuments) {
    return null;
  }

  const handleReorder = (reorderedDocuments: AmendmentDocument[]) => {
    if (reorderedDocuments.length === 0) {
      return;
    }

    setPendingDocuments(reorderedDocuments);
  };

  const handleSave = async () => {
    if (
      !pendingDocuments ||
      !hasDocumentOrderChanged(relatedDocuments, pendingDocuments)
    ) {
      return;
    }

    await updateDocumentVersions(relatedDocuments, pendingDocuments);
    if (willGenerateCombinedSummary) {
      onSummaryStarted();
    }
    setPendingDocuments(null);
  };

  const handleDocumentsChanged = (changes: DocumentChange[]) => {
    let reordered = [...displayedDocuments];

    // Process all changes in order
    for (const change of changes) {
      if (change.action === 'add') {
        // Add to version chain - newly added doc becomes most recent (top of list)
        reordered = [
          change.document, // the newly-added doc
          ...reordered.filter((d) => d.id !== change.document.id), // avoid duplicates
        ];
      } else if (change.action === 'remove') {
        // Remove from version chain
        reordered = reordered.filter((d) => d.id !== change.document.id);
      }
    }

    setPendingDocuments(reordered);
  };

  return (
    <>
      <Accordion
        variant="default"
        title={`Manage ${isWillDocument ? 'codicils' : 'amendments'}`}
        description={
          <>
            Add and chronologically order any{' '}
            {isWillDocument ? 'codicils' : 'amendments'} along with the original
            document. This ordering determines precedence for summaries.
          </>
        }
        isDefaultExpanded={accordionExpanded}
        onChange={handleAccordionChange}
        withDivider={false}
      >
        <Stack spacing={2}>
          {shouldShowCallout && (
            <Callout severity="info-high">
              The combined summary will use the newest{' '}
              {primaryDocumentTypeName.toLowerCase()} in the list, along with
              any amendments placed above it. Any items listed below that{' '}
              {primaryDocumentTypeName.toLowerCase()} will not be included.
            </Callout>
          )}

          <Stack>
            <Box sx={{ backgroundColor: COLORS.GRAY[50], p: 1.5 }}>
              <HeaderCellTypography>
                Order by newest to oldest
              </HeaderCellTypography>
            </Box>

            <DragAndDropList
              key={displayedDocumentIds.join()}
              respondToLengthChanges={false}
              disabled={!canEditHouseholdData || displayedDocuments.length <= 1}
              items={displayedDocuments}
              ItemInnerComponent={AmendmentItem}
              onDropped={handleReorder}
              emptyStateMessage="No related documents found"
            />
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            {canEditHouseholdData && (
              <>
                <Tooltip
                  title="Saving these changes will generate a new combined summary."
                  show={willGenerateCombinedSummary}
                >
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!hasPendingChanges || isSavingDocumentVersions}
                    onClick={handleSave}
                  >
                    {willGenerateCombinedSummary ? 'Save and generate' : 'Save'}
                  </Button>
                </Tooltip>
                <ManageDocumentsPopover
                  selectable={selectable}
                  selectedIds={displayedDocumentIds}
                  onDocumentsChanged={handleDocumentsChanged}
                  onAddNewDocument={handleAddNewDocument}
                />
              </>
            )}
          </Stack>
        </Stack>
      </Accordion>

      {householdId && (
        <StandaloneDocumentUploaderModal
          householdId={householdId}
          uploadModalOpen={isUploaderOpen}
          onModalClose={handleUploaderClose}
        />
      )}
    </>
  );
}
